import React, { useEffect, useMemo, useState } from 'react';

import Tabla from "../../../../components/ui/materialui/tabla";
import TableHeader from "../../../../components/ui/materialui/tableHeader";
import MButton from '../../../../components/inputs/yup/materialui/mButton';

import Grid2 from '@mui/material/Grid2';
import AddTwoToneIcon from '@mui/icons-material/AddTwoTone';
import SummarizeTwoToneIcon from '@mui/icons-material/SummarizeTwoTone'

import { exportExcel } from '../../../../utils/exportExcel';
import { enumTipoParticipante, TipoFuncionalidad } from '../../../../utils/enums';
import { columns } from "../../../../dtos/columns";
import { numericFormatter } from 'react-number-format';
import { useCodeudor } from '../../hooks/useCodeudor';
import { SeguroCodeudorEdit } from '../../models/seguroCodeudorEdit';
import AccionesGrillaSegurosCodeudor from './accionesGrillaSegurosCodeudor';
import ModalSeguroCodeudor from './modalSeguroCodeudor';
import { SeguroCodeudor } from '../../models/seguroCodeudor';
import { useDetalleMutuoPermissions } from '../../hooks/useDetalleMutuoPermissions';
import { DetalleMutuoPermissionGuard } from '../../../../components/auth/DetalleMutuoPermissionGuard';
import { PermissionGuard } from '../../../../components/auth/ProtectedRoute';

interface IProps {
    idMutuo: number;
    rut: string | null;
    porcentajedeudaseguro: number;
    esVigente?: boolean;
}

export interface SeguroRow {
    TipoParticipante: enumTipoParticipante;
    MutuoId: number;
    SeguroId?: number | undefined;
    RutAsegurado?: string | undefined;
    TipoSeguro: string;
    CodigoPoliza: string;
    CompaniaSeguros?: string | undefined;
    Descripcion: string | null | undefined;
    Tasa: number;
    MontoPrimaAnual: number;
    MontoAsegurado: number;
    MontoPrimaDividendo: number;
}

const ListaSegurosCodeudor: React.FC<IProps> = (props) => {

    const { obtenerSegurosCodeudor } = useCodeudor();
    const [segurosRows, setSegurosRows] = useState<SeguroRow[]>([]);
    const [seguroCodeudorSeleccionado, setSeguroCodeudorSeleccionado] = useState<SeguroCodeudorEdit>();
    const [showModal, setShowModal] = useState<boolean>(false);
    const [esNuevoSeguro, setEsNuevoSeguro] = useState<boolean>(false);
    const { permisoBandeja, permisoCarteraInfo } = useDetalleMutuoPermissions(props.esVigente);

    const cargarSegurosCodeudor = async () => {

        if (props.rut) {
            obtenerSegurosCodeudor(props.idMutuo, props.rut)
                .then((seguros: SeguroCodeudor[]) => {

                    const dataRowMapping: SeguroRow[] = seguros.map(seguro => ({
                        TipoParticipante: enumTipoParticipante.Codeudor,
                        MutuoId: props.idMutuo,
                        RutAsegurado: seguro.rut,
                        TipoSeguro: seguro.tipoSeguro,
                        CodigoPoliza: seguro.codigoPoliza,
                        CompaniaSeguros: seguro.companiaSeguros,
                        Descripcion: seguro.descripcion,
                        Tasa: seguro.tasa,
                        MontoPrimaAnual: seguro.montoPrimaAnual,
                        MontoAsegurado: seguro.montoAsegurado,
                        MontoPrimaDividendo: seguro.montoPrimaDividendo
                    }));

                    setSegurosRows(dataRowMapping);

                });
        }
        else {
            setSegurosRows([]);
        }

    }

    const renderizar = () => {
        cargarSegurosCodeudor();
    }

    const COLUMNS: columns<SeguroRow>[] = [
        {
            name: 'Acciones',
            selector: (row) => '',
            sortable: true,
            wrap: true,
            export: false,
            cell: (row) => <AccionesGrillaSegurosCodeudor
                seguroRow={row}
                setSeguroCodeudorSeleccionado={setSeguroCodeudorSeleccionado}
                setShowModal={setShowModal}
                renderizar={renderizar}
                esVigente={props.esVigente ?? false}
            />,
            allowOverflow: true,
            button: true,
        },
        {
            name: 'Tipo seguro',
            selector: (row) => row.TipoSeguro,
            sortable: true,
            sortField: 'TipoSeguro',
            wrap: true,
            export: true,
            center: true,
        },
        {
            name: 'Código poliza',
            selector: (row) => row.CodigoPoliza,
            sortable: true,
            sortField: 'CodigoPoliza',
            wrap: true,
            export: true,
            center: true,
        },
        {
            name: 'Compañía de seguros',
            selector: (row) => row.CompaniaSeguros,
            sortable: true,
            sortField: 'CompaniaSeguros',
            wrap: true,
            export: true,
            center: false,
        },
        {
            name: 'Descripción',
            selector: (row) => row.Descripcion,
            sortable: true,
            sortField: 'Descripcion',
            wrap: true,
            export: true,
            center: false,
        },
        {
            name: 'Tasa',
            selector: (row) => row.Tasa,
            cell: (row) => numericFormatter(row.Tasa.toString(), { "thousandsGroupStyle": "thousand", "thousandSeparator": ".", "decimalSeparator": ",", "suffix": " %" }),
            sortable: true,
            sortField: 'Tasa',
            wrap: true,
            export: true,
            right: true,
        },
        {
            name: 'Prima anual',
            selector: (row) => row.MontoPrimaAnual,
            cell: (row) => numericFormatter(row.MontoPrimaAnual.toString(), { "thousandsGroupStyle": "thousand", "thousandSeparator": ".", "decimalSeparator": ",", "prefix": "UF " }),
            sortable: true,
            sortField: 'MontoPrimaAnual',
            wrap: true,
            export: true,
            right: true,
        },
        {
            name: 'Monto asegurado',
            selector: (row) => row.MontoAsegurado,
            cell: (row) => numericFormatter(row.MontoAsegurado.toString(), { "thousandsGroupStyle": "thousand", "thousandSeparator": ".", "decimalSeparator": ",", "prefix": "UF " }),
            sortable: true,
            sortField: 'MontoAsegurado',
            wrap: true,
            export: true,
            right: true,
        },
        {
            name: 'Prima dividendo',
            selector: (row) => row.MontoPrimaDividendo,
            cell: (row) => numericFormatter(row.MontoPrimaDividendo.toString(), { "thousandsGroupStyle": "thousand", "thousandSeparator": ".", "decimalSeparator": ",", "prefix": "UF " }),
            sortable: true,
            sortField: 'MontoPrimaDividendo',
            wrap: true,
            export: true,
            right: true,
        },
    ];



    const columns = useMemo(() => COLUMNS, [segurosRows, props.esVigente]);

    useEffect(() => {

        cargarSegurosCodeudor();

    }, [props.idMutuo, props.esVigente]);


    return (

        <>
            <Grid2 sx={{ mb: 3, mt: 2, p: 2 }}>
                <Tabla
                    columns={columns}
                    data={segurosRows}
                    titulo={<TableHeader
                        button={
                            <DetalleMutuoPermissionGuard
                                esVigente={props.esVigente}
                                permissions={[TipoFuncionalidad.Bandeja_editar]}>
                                <MButton
                                    type='button'
                                    variant="contained"
                                    color="primary"
                                    startIcon={<AddTwoToneIcon />}
                                    onClick={() => {
                                        setShowModal(true);
                                        setEsNuevoSeguro(true);
                                        setSeguroCodeudorSeleccionado(undefined);
                                    }}
                                    disabled={!permisoBandeja && !permisoCarteraInfo}
                                >
                                    Agregar
                                </MButton>
                            </DetalleMutuoPermissionGuard>
                        }
                        buttonExport={
                            <>
                            <PermissionGuard permissions={[TipoFuncionalidad.Transversal_exportar]}>
                                <MButton
                                    type="button"
                                    variant='outlined'
                                    startIcon={<SummarizeTwoToneIcon />}
                                    onClick={() => exportExcel(segurosRows, columns, "Listado-Seguros")}
                                >
                                    Exportar
                                </MButton>
                                </PermissionGuard>
                            </>
                        }
                    />}
                />
            </Grid2>

            {props.rut != null && (seguroCodeudorSeleccionado || esNuevoSeguro) &&
                <ModalSeguroCodeudor
                    seguroCodeudorSeleccionado={seguroCodeudorSeleccionado}
                    porcentajedeudaseguro={props.porcentajedeudaseguro}
                    mutuoId={props.idMutuo}
                    rutCodeudor={props.rut!}
                    esNuevoSeguro={esNuevoSeguro}
                    show={showModal}
                    setShow={setShowModal}
                    onHide={() => {
                        setShowModal(false);
                        cargarSegurosCodeudor();
                        setEsNuevoSeguro(false);
                        setSeguroCodeudorSeleccionado(undefined);
                    }}
                    esVigente={props.esVigente}
                />

            }

        </>
    );
};

export default ListaSegurosCodeudor;


